import { TimelineStep } from '@innovation-os-ng/ui/chart-ng';
import * as d3 from 'd3';

export const ONE_DAY_MILLISECONDS = 1000 * 60 * 60 * 24;

export const calculateTimeSteps: (
  startTimestamp: number,
  endTimestamp: number
) => {
  dates: Array<number>;
  type: TimelineStep;
} = (startTimestamp: number, endTimestamp: number) => {
  // in order to generate the steps, which are alway "end of days"
  // we want to make sure that we include the starting day by navigating to the end of the day before
  const start = new Date(getStartOfTheDay(startTimestamp) - 1);
  // and we want to make sure to include the end of the ending day as well
  const end = new Date(getEndOfTheDay(endTimestamp));

  const steps = getDateSteps(start, end);

  return {
    dates: steps.dates.map((date) => date.getTime()),
    type: steps.type
  };
};

const getDateSteps: (
  start: Date,
  end: Date
) => {
  dates: Array<Date>;
  type: TimelineStep;
} = (start: Date, end: Date) => {
  const MIN_POINTS = 16;

  const yearSteps = createInterval(start, end, d3.timeYear);
  if (yearSteps.length > MIN_POINTS) {
    return {
      type: TimelineStep.YEAR,
      dates: yearSteps
    };
  }
  const monthSteps = createInterval(start, end, d3.timeMonth);
  if (monthSteps.length > MIN_POINTS) {
    return {
      type: TimelineStep.MONTH,
      dates: monthSteps
    };
  }
  const weekSteps = createInterval(start, end, d3.timeMonday); // use d3.timeWeek if week should start on Sunday
  if (weekSteps.length > MIN_POINTS) {
    return {
      type: TimelineStep.WEEK,
      dates: weekSteps
    };
  }
  return {
    type: TimelineStep.DAY,
    dates: createInterval(start, end, d3.timeDay)
  };
};

const createInterval: (
  start: Date,
  end: Date,
  d3TimeInterval: {
    range: (start: Date, end: Date) => Array<Date>;
    ceil: (date: Date) => Date;
    floor: (date: Date) => Date;
  }
) => Array<Date> = (
  start: Date,
  end: Date,
  d3TimeInterval: {
    range: (start: Date, end: Date) => Array<Date>;
    ceil: (date: Date) => Date;
    floor: (date: Date) => Date;
  }
) => {
  const modifyAndCheckDate = (check: Date, startDate: Date, endDate: Date) => {
    // this little trick adjusts the date ever so slightly that we get
    // the end of the days that we want
    // the end of the week that we want
    //    beware d3.timeWeek starts on sunday so this will give us end of saturday. There is d3.timeMonday and any other day as well!
    // the end of the months that we want
    // the end of the years that we want
    // -> since d3 returns the start of every time frame but instead we want the end of the previous!
    // -> the filter logic built in the calling function will make sure we only get the ones within boundaries!
    check.setMilliseconds(-1);
    return check.getTime() > startDate.getTime() && check.getTime() < endDate.getTime();
  };

  return [
    start,
    // range is including the start excluding the end. So CEIL will make sure we get the end of the first interval
    // and likely go out of bounds for the end. the filter will take care of it
    ...d3TimeInterval.range(d3TimeInterval.ceil(start), d3TimeInterval.ceil(end)).filter((d) => modifyAndCheckDate(d, start, end)),
    end
  ];
};

export const getEndOfTheDay: (date: number) => number = (date) => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end.getTime();
};

export const getStartOfTheDay: (date: number) => number = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start.getTime();
};

export const timestampToDateString: (date: number) => string = (date) => {
  const dateObj = new Date(date);
  const month = dateObj.getMonth() + 1 > 9 ? dateObj.getMonth() + 1 : `0${dateObj.getMonth() + 1}`;
  const dateStr = dateObj.getDate() > 9 ? dateObj.getDate() : `0${dateObj.getDate()}`;
  return `${dateObj.getFullYear()}-${month}-${dateStr}`;
};

export const getDatesDiff: (date1: Date, date2: Date) => number = (date1, date2) => date2.getTime() - date1.getTime();
